import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { VaModal } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import environment from 'platform/utilities/environment';
import { isLoggedIn } from 'platform/user/selectors';
import { formatSignInTimestamp } from '../hooks';
import { getPasskeyProvider } from '../passkeyProviders';
import { PasskeyAlert, usePasskeyAlert } from './PasskeyAlertSlot';

const PASSKEY_API_BASE = environment.API_URL;
const REGISTRATIONS_URL = `${PASSKEY_API_BASE}/sign_in/webauthn/registrations`;

function transformPasskey(credential) {
  return {
    credentialId: credential.credential_id,
    aaguid: credential.aaguid,
    createdAt: credential.created_at,
    lastUsedAt: credential.last_used_at,
  };
}

function usePasskeys(loggedIn, refreshKey) {
  const [passkeys, setPasskeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Cache the CSRF token returned from the GET response for use in DELETE
  const csrfTokenRef = useRef(
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('csrfToken')
      : null,
  );

  useEffect(() => {
    if (!loggedIn) return;

    fetch(REGISTRATIONS_URL, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(res => {
        const token = res.headers.get('X-CSRF-Token');
        if (token) csrfTokenRef.current = token;
        return res.json();
      })
      .then(data => {
        if (!Array.isArray(data?.webauthn_credentials)) {
          throw new Error('Unexpected response from registrations endpoint');
        }
        setPasskeys(data.webauthn_credentials.map(transformPasskey));
        setError(null);
      })
      .catch(err => setError(err))
      .finally(() => setIsLoading(false));
  }, [loggedIn, refreshKey]);

  const deletePasskey = useCallback(
    credentialId =>
      fetch(`${REGISTRATIONS_URL}/${credentialId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token':
            csrfTokenRef.current || localStorage.getItem('csrfToken'),
        },
      }).then(res => {
        if (!res.ok) throw new Error('Failed to delete passkey');
        return res.json();
      }),
    [],
  );

  const removePasskey = useCallback(credentialId => {
    setPasskeys(prev => prev.filter(p => p.credentialId !== credentialId));
  }, []);

  return { passkeys, isLoading, error, deletePasskey, removePasskey };
}

function PasskeyCard({ passkey, onRequestDelete }) {
  const { credentialId, aaguid, createdAt, lastUsedAt } = passkey;
  const { name: providerName } = getPasskeyProvider(aaguid);
  const createdDisplay = formatSignInTimestamp(createdAt);
  const lastUsedDisplay = formatSignInTimestamp(lastUsedAt);
  // Avoid saying "passkey" twice when the provider name already ends with it.
  const deleteLabel = /passkey$/i.test(providerName)
    ? `Delete ${providerName}`
    : `Delete ${providerName} passkey`;

  return (
    <va-card class="passkey-management__card vads-u-margin-bottom--2">
      <h3 className="vads-u-margin--0">{providerName}</h3>
      {createdDisplay && (
        <p className="vads-u-margin-y--0p5">Created on {createdDisplay}</p>
      )}
      {lastUsedDisplay && (
        <p className="vads-u-margin-y--0p5">Last used on {lastUsedDisplay}</p>
      )}
      <va-button
        class="vads-u-margin-top--1"
        text="Delete passkey"
        label={deleteLabel}
        secondary
        onClick={() => onRequestDelete(credentialId)}
      />
    </va-card>
  );
}

PasskeyCard.propTypes = {
  passkey: PropTypes.shape({
    credentialId: PropTypes.string.isRequired,
    aaguid: PropTypes.string,
    createdAt: PropTypes.string,
    lastUsedAt: PropTypes.string,
  }).isRequired,
  onRequestDelete: PropTypes.func.isRequired,
};

export default function PasskeyManagement({
  refreshKey = 0,
  onDeleteFlowStart,
}) {
  const loggedIn = useSelector(isLoggedIn);
  const { passkeys, isLoading, error, deletePasskey, removePasskey } =
    usePasskeys(loggedIn, refreshKey);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  // { status, key } kept in one state value: React 17 doesn't batch updates
  // after an await, so separate setters can remount the alert mid-update.
  const [deleteResult, setDeleteResult] = useState(null);
  const resultKeyRef = useRef(0);
  const sharedAlert = usePasskeyAlert();

  const showDeleteResult = status => {
    resultKeyRef.current += 1;
    // A new key remounts PasskeyAlert so focus moves to it exactly once.
    setDeleteResult({ status, key: resultKeyRef.current });
  };

  const handleRequestDelete = useCallback(
    credentialId => {
      onDeleteFlowStart?.();
      // Clear any prior delete alert so a stale result isn't left behind.
      setDeleteResult(null);
      setPendingDeleteId(credentialId);
    },
    [onDeleteFlowStart],
  );

  let alertNode = null;
  if (deleteResult?.status === 'success') {
    alertNode = (
      <PasskeyAlert
        key={deleteResult.key}
        status="success"
        headline="You successfully deleted your passkey"
      >
        <p>You can create a new passkey anytime.</p>
      </PasskeyAlert>
    );
  } else if (deleteResult?.status === 'error') {
    alertNode = (
      <PasskeyAlert
        key={deleteResult.key}
        status="error"
        headline="We could not delete your passkey"
      >
        <p>Please try again later.</p>
      </PasskeyAlert>
    );
  }

  // Push into the shared alert region when present so alerts replace, not stack.
  const alertNodeRef = useRef(alertNode);
  alertNodeRef.current = alertNode;
  useEffect(() => {
    if (!sharedAlert) return undefined;
    sharedAlert.setAlert(alertNodeRef.current);
    return () => sharedAlert.setAlert(null);
  }, [sharedAlert, deleteResult]);

  const handleConfirmDelete = async () => {
    const credentialId = pendingDeleteId;
    setPendingDeleteId(null);
    setDeleteResult(null);

    try {
      await deletePasskey(credentialId);
      removePasskey(credentialId);
      showDeleteResult('success');
    } catch {
      showDeleteResult('error');
    }
  };

  if (!loggedIn || isLoading) {
    return <va-loading-indicator message="Loading your passkeys..." />;
  }

  if (error) {
    return (
      <va-alert status="error" visible>
        <h2 slot="headline">We could not load your passkeys</h2>
        <p>Please try again later.</p>
      </va-alert>
    );
  }

  return (
    <>
      {!sharedAlert && alertNode}

      {passkeys.length !== 0 && (
        <h2 className="vads-u-margin-top--3">Your passkeys</h2>
      )}
      {passkeys.map(passkey => (
        <PasskeyCard
          key={passkey.credentialId}
          passkey={passkey}
          onRequestDelete={handleRequestDelete}
        />
      ))}

      <VaModal
        status="warning"
        modalTitle="Delete this passkey?"
        visible={!!pendingDeleteId}
        onCloseEvent={() => setPendingDeleteId(null)}
        onPrimaryButtonClick={handleConfirmDelete}
        onSecondaryButtonClick={() => setPendingDeleteId(null)}
        primaryButtonText="Yes, delete"
        secondaryButtonText="No, go back"
      >
        <p>
          You will no longer be able to use this passkey to sign in to VA.gov.
          You can always create a new one.
        </p>
      </VaModal>
    </>
  );
}

PasskeyManagement.propTypes = {
  refreshKey: PropTypes.number,
  onDeleteFlowStart: PropTypes.func,
};
